import { useCallback, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { CONTEXT_AUTH_TOKEN_KEY } from '../app/apollo/getTransportLink';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import { gql } from '../graphql/generated/gql';
import useShareSecret from '../post/useShareSecret';
import { SessionActions } from './sessionSlice';
import visitorAuthTokenUtil from './visitorAuthTokenUtil';

const VISITOR_SESSION = gql(/** @complexity 2 */ `
  query VisitorSession {
    visitorSession: currentVisitorSession {
      ...startVisitorSession_session
    }
  }
`);

const CREATE_VISITOR = gql(/** @complexity 4 */ `
  mutation createVisitor($postId: ID, $postShareSecret: String) {
    createVisitor(postId: $postId, postShareSecret: $postShareSecret) {
      ...startVisitorSession_session
    }
  }
`);

const useVisitorSession = (postId: string) => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.session.user);
  const postShareSecret = useShareSecret();

  const visitorAuthToken = visitorAuthTokenUtil.get();
  const { data, loading, refetch } = useQuery(VISITOR_SESSION, {
    skip: !visitorAuthToken,
    context: { [CONTEXT_AUTH_TOKEN_KEY]: visitorAuthToken },
  });

  const [createVisitorMutation] = useMutation(CREATE_VISITOR, {
    variables: postShareSecret ? { postId, postShareSecret } : undefined,
  });

  const visitorSession = data?.visitorSession;

  useEffect(() => {
    if (visitorSession && !currentUser.id) {
      dispatch(SessionActions.startVisitorSession(visitorSession));
    }
  }, [currentUser.id, dispatch, visitorSession]);

  const createVisitor = useCallback(async () => {
    if (visitorSession) {
      return;
    }
    const createVisitorRes = await createVisitorMutation();
    const session = createVisitorRes.data?.createVisitor;
    if (session) {
      dispatch(SessionActions.startVisitorSession(session));
      await refetch();
    }
  }, [createVisitorMutation, dispatch, refetch, visitorSession]);

  return {
    visitorSession,
    ensureVisitorSession: loading ? null : createVisitor,
  };
};

export default useVisitorSession;
